// ==============================
// Imports
// ==============================
import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Snackbar,
  TextField,
  Typography,
  LinearProgress,
  Divider,
  useTheme,
  useMediaQuery,
  Grid,
  Button,
  Chip,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";

import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import theme from "../theme";

import { ReactComponent as Goal } from "../assets/icons/goal.svg";
import { ReactComponent as Share } from "../assets/icons/share.svg";
import TicketCard from "./TicketCard";
import LoadingScreen from "./LoadingScreen";

// Component: DayEndReport
const DayEndReport = () => {
  const navigate = useNavigate();
  const resTheme = useTheme();
  const isMobile = useMediaQuery(resTheme.breakpoints.down("md"));

  // Context - Get logged in user info
  const { userInfo, userGoals } = useContext(AutoTaskAuthContext) || {};
  const { name = null, mobile = null, id = null } = userInfo || {};
  const user_id = id;

  const [assignedTicket, setAssignedTicket] = useState([]);

  // Snackbar + Dialog
  const [reportDailogOpen, setReportDailog] = useState(false);
  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const [isSending, setIsSending] = useState(false);

  // Goals state (from userGoals only)
  const [goals, setGoals] = useState([]);
  const [errors, setErrors] = useState({});

  // ---------- Yes/No feature ----------
  const [yesNoQuestions, setYesNoQuestions] = useState([]);
  const [yesNoSubmitted, setYesNoSubmitted] = useState(false);
  const [yesNoAnswers, setYesNoAnswers] = useState({});
  // ------------------------------------

  useEffect(() => {
    if (Array.isArray(userGoals) && userGoals.length > 0) {
      setGoals(
        userGoals
          .filter((goal) => goal.task_type === "Daily")
          .map((goal) => ({
            user_task_id: goal.task_id,
            goal_name: goal.task_name,
            target: goal.target,
            report_date: new Date().toISOString().split("T")[0],
            completed: 0,
            remarks: "",
          })),
      );
    } else {
      setGoals([]);
    }
  }, [userGoals]);

  // Fetch tickets assigned to user
  useEffect(() => {
    const fetchAssignedTickets = async () => {
      try {
        const urlForAssigned = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/ticketbyassigneduser?assigned_to=${user_id}`;
        const response = await axios.get(urlForAssigned);
        setAssignedTicket(response.data.data || []);
      } catch (error) {
        console.error("Error fetching assigned tickets", error);
      }
    };

    if (user_id) fetchAssignedTickets();
  }, [user_id]);

  // ---------- Fetch today's Yes/No questions ----------
  useEffect(() => {
    const fetchYesNo = async () => {
      if (!user_id) return;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/yes-no/today`,
          { params: { user_id, store_id: userInfo.store_id } },
        );
        if (res.data.success) {
          const { daily_questions, submitted_today } = res.data.data;
          setYesNoQuestions(daily_questions);
          setYesNoSubmitted(submitted_today);

          const preAnswers = {};
          daily_questions.forEach((q) => {
            if (q.answered) {
              preAnswers[q.id] = q.answer;
            }
          });
          setYesNoAnswers(preAnswers);
        }
      } catch (err) {
        console.error("Fetch Yes/No failed", err);
      }
    };
    fetchYesNo();
  }, [user_id, userInfo?.store_id]);

  // Get today's date in "en-CA" format
  const today = new Date().toLocaleDateString("en-CA");

  // Filter tickets by today's date and time_taken > 0
  const filteredTickets = assignedTicket.filter((ticket) => {
    const ticketDate = new Date(ticket.updated_at).toLocaleDateString("en-CA");
    return ticketDate === today && ticket.time_taken;
  });

  // Separate regular and follow-up tickets
  const regularTickets = filteredTickets.filter(
    (ticket) => ticket.is_follow_up !== 1,
  );
  const followUpTickets = filteredTickets.filter(
    (ticket) => ticket.is_follow_up === 1,
  );

  const totalTimeBudget = filteredTickets.reduce(
    (sum, ticket) => sum + Number(ticket.time_budget || 0),
    0,
  );
  const totalTimeTaken = filteredTickets.reduce(
    (sum, ticket) => sum + Number(ticket.time_taken || 0),
    0,
  );

  // Handle goal input change
  const handleGoalChange = (index, field, value) => {
    setGoals((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`remark-${index}`];
      return newErrors;
    });
  };

  // Validation
  const validateGoals = () => {
    let newErrors = {};
    let firstErrorMessage = "";

    goals.forEach((goal, index) => {
      const completedValue = Number(goal.completed);

      if (completedValue === 0 && !goal.remarks.trim()) {
        newErrors[`remark-${index}`] = "Enter the remark";
        if (!firstErrorMessage) {
          firstErrorMessage = `Please enter the remark for ${goal.goal_name}`;
        }
      }
    });

    setErrors(newErrors);

    if (firstErrorMessage) {
      setSnackBarConfig({
        open: true,
        message: firstErrorMessage,
        severity: "error",
      });
    }

    return Object.keys(newErrors).length === 0;
  };

  // Close snackbar
  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackBarConfig((prev) => ({ ...prev, open: false }));
  };

  // Submit Report (with Yes/No integration)
  const handleSaveAndShare = async () => {
    if (!validateGoals()) return;

    let yesNoEmailData = [];

    if (!yesNoSubmitted && yesNoQuestions.length > 0) {
      const answersPayload = yesNoQuestions
        .map((q) => ({
          question_id: q.id,
          answer: yesNoAnswers[q.id] === 1 ? true : false,
        }))
        .filter((a) => a.answer !== undefined);

      if (answersPayload.length > 0) {
        try {
          const yesNoSubmitUrl = `${process.env.REACT_APP_API_BASE_URL}/api/yes-no/submit`;
          const yesNoPayload = {
            user_id,
            store_id: userInfo.store_id,
            answers: answersPayload,
          };
          const yesNoRes = await axios.post(yesNoSubmitUrl, yesNoPayload);
          if (!yesNoRes.data.success) {
            setSnackBarConfig({
              open: true,
              message: yesNoRes.data.statusMessage,
              severity: "error",
            });
            return;
          }
          setYesNoSubmitted(true);
        } catch (error) {
          setSnackBarConfig({
            open: true,
            message: "Failed to submit Yes/No answers",
            severity: "error",
          });
          return;
        }
      }
    }

    yesNoEmailData = yesNoQuestions.map((q) => ({
      question_text: q.question_text,
      answer: yesNoAnswers[q.id] === 1 ? 1 : 0,
      question_type: q.type,
    }));

    try {
      setIsSending(true);

      const saveGoalsUrl = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/logs`;
      const goalResponse = await axios.post(saveGoalsUrl, goals);

      if (goalResponse?.data?.success) {
        const shareReportUrl = `${process.env.REACT_APP_API_BASE_URL}/api/auth/sendSummaryEmail`;

        // Build tasks array – include is_follow_up flag
        const tasks = filteredTickets.map((ticket) => ({
          ticket_number: ticket.ticket_number,
          department_name: ticket.department_name,
          description: ticket.description,
          status: ticket.status,
          time_taken: ticket.time_taken,
          time_budget: ticket.time_budget,
          created_by_name: ticket.created_by_name,
          store_name: ticket.store_name,
          store_id: ticket.store_id,
          is_follow_up: ticket.is_follow_up,
        }));

        const goalsData = goals.map((g) => {
          const result = Number(g.completed) || 0;
          const progress = g.target ? Math.round((result / g.target) * 100) : 0;

          return {
            goal_name: g.goal_name,
            target: g.target,
            result: result,
            remark: g.remarks || "",
            progress: progress,
          };
        });
        const userEmail = userInfo.email;
        const assistentEmail = "bindukumudu25@gmail.com";
        const payload = {
          name,
          mobile,
          tasks,
          goals: goalsData,
          bcc: [userEmail, assistentEmail],
          yesNoAnswers: yesNoEmailData,
        };

        const reportResponse = await axios.post(shareReportUrl, payload);
        if (reportResponse.data.success) {
          setSnackBarConfig({
            open: true,
            message: "Day End Report sent!!!",
            severity: "success",
          });
        }
        setReportDailog(true);
      } else {
        setSnackBarConfig({
          open: true,
          message: "Unable to send Report",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error in handleSaveAndShare:", error);
      setSnackBarConfig({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      {isSending && <LoadingScreen />}

      {/* Ticket List */}
      <Box>
        <Typography sx={{ mb: 1, fontWeight: "bold", mt: !isMobile ? 2 : 0 }}>
          Today's Tickets
        </Typography>
        {filteredTickets.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            You haven’t worked on any tickets today.
          </Typography>
        )}
        {filteredTickets.length > 0 && (
          <Box
            sx={{
              mb: 2,
              borderRadius: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              py: 1,
              background:
                "linear-gradient(90deg,rgba(0, 173, 173, 1) 0%, rgba(68, 40, 160, 1) 100%)",
            }}
          >
            <Box textAlign="center" flex={1}>
              <Typography variant="caption" color="#fff" fontWeight={500}>
                Total Time Budget
              </Typography>
              <Typography fontSize={14} fontWeight={700} color="#fff">
                {totalTimeBudget} Mins
              </Typography>
            </Box>

            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: "#90CAF9" }}
            />

            <Box textAlign="center" flex={1}>
              <Typography variant="caption" color="#fff" fontWeight={500}>
                Total Time Taken
              </Typography>
              <Typography fontSize={14} fontWeight={700} color="#fff">
                {totalTimeTaken} Mins
              </Typography>
            </Box>
          </Box>
        )}

        {/* Regular Tickets Section */}
        {regularTickets.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              sx={{ mb: 1, fontWeight: 600, color: "#1f2937" }}
            >
              Resolved Tickets
            </Typography>
            <Grid container spacing={2}>
              {regularTickets.map((ticket, index) => (
                <Grid item key={index} xs={12} sm={isMobile ? 12 : 4}>
                  <TicketCard ticket={ticket} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Follow-up Tickets Section – simple custom card */}
        {followUpTickets.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 600, color: "#1f2937" }}
              >
                Follow‑up Resolved Tickets
              </Typography>
              <Chip
                label="Follow‑up"
                size="small"
                sx={{
                  bgcolor: "#fef3c7",
                  color: "#92400e",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              />
            </Box>
            <Grid container spacing={2}>
              {followUpTickets.map((ticket, index) => (
                <Grid item key={index} xs={12} sm={isMobile ? 12 : 4}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid #e0e0e0",
                      bgcolor: "#fff9f0", // light warm background to stand out
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      #{ticket.ticket_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ticket.store_name || "N/A"}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, fontStyle: "italic" }}
                    >
                      {ticket.description}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="caption">
                        Time taken: {ticket.time_taken} min
                      </Typography>
                      <Typography variant="caption">
                        Budget: {ticket.time_budget} min
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>

      {/* Yes/No Questions Section */}
      {yesNoQuestions.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Daily Yes / No Questions
            </Typography>
            {yesNoSubmitted && (
              <Typography variant="caption" sx={{ color: "green" }}>
                (Already submitted)
              </Typography>
            )}
          </Box>

          {yesNoQuestions.map((q) => {
            const userAnswer = yesNoAnswers[q.id];
            const isYes = userAnswer === 1;
            const isNo = userAnswer === 0;

            return (
              <Box
                key={q.id}
                sx={{
                  mb: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  p: 1,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                }}
              >
                <Typography>{q.question_text}</Typography>

                {yesNoSubmitted ? (
                  <Typography
                    sx={{
                      fontWeight: 600,
                      color: isYes ? "#16a34a" : "#dc2626",
                      fontSize: 14,
                    }}
                  >
                    {isYes ? "✅ Yes" : "❌ No"}
                  </Typography>
                ) : (
                  <Box>
                    <Button
                      variant={isYes ? "contained" : "outlined"}
                      size="small"
                      onClick={() =>
                        setYesNoAnswers((prev) => ({ ...prev, [q.id]: 1 }))
                      }
                      sx={{ mr: 1 }}
                    >
                      Yes
                    </Button>
                    <Button
                      variant={isNo ? "contained" : "outlined"}
                      size="small"
                      onClick={() =>
                        setYesNoAnswers((prev) => ({ ...prev, [q.id]: 0 }))
                      }
                    >
                      No
                    </Button>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Goal Section */}
      <Box>
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Goal />
          <Typography sx={{ mb: 1, fontWeight: "bold" }}>
            Daily Goals
          </Typography>
        </Box>
        {userGoals.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No goals have been assigned yet.
          </Typography>
        )}

        <Box sx={{ mt: 2 }}>
          {goals.map((row, index) => {
            const completed = goals[index]?.completed ?? 0;
            const remarks = goals[index]?.remarks ?? "";
            const percentage =
              row.target && completed !== undefined
                ? Math.round((Number(completed) / Number(row.target)) * 100)
                : 0;

            return (
              <Box
                key={row.task_id || index}
                sx={{
                  p: 2,
                  mb: 1,
                  bgcolor: "#fff",
                  borderRadius: 2,
                  border: "1px solid #D9D9D9",
                }}
              >
                {/* Goal Header */}
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: "bold", fontSize: 16, mb: 1 }}
                  >
                    {row.goal_name}
                  </Typography>
                  <Typography sx={{ fontSize: "16px" }}>
                    Goal:{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        color: theme.lightTheme.themeCol,
                      }}
                    >
                      {row.target}
                    </span>
                  </Typography>
                </Box>

                {/* Inputs */}
                <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                  <TextField
                    type="number"
                    label="Completed"
                    size="small"
                    value={completed}
                    onChange={(e) =>
                      handleGoalChange(index, "completed", e.target.value)
                    }
                    sx={{ flex: 1, ...formStyle }}
                    inputProps={{ min: 0 }}
                  />
                  <TextField
                    label="Remarks"
                    size="small"
                    value={remarks}
                    onChange={(e) =>
                      handleGoalChange(index, "remarks", e.target.value)
                    }
                    sx={{ flex: 2, ...formStyle }}
                    error={Boolean(errors[`remark-${index}`])}
                    helperText={errors[`remark-${index}`]}
                  />
                </Box>

                {/* Progress */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      fontSize: 12,
                      mt: -1,
                      color:
                        percentage > 100
                          ? theme.lightTheme.performanceProgressBars.excellent
                          : percentage < 60
                          ? theme.lightTheme.performanceProgressBars.poor
                          : percentage < 80
                          ? theme.lightTheme.performanceProgressBars.average
                          : theme.lightTheme.performanceProgressBars.good,
                      fontWeight: 500,
                    }}
                  >
                    {percentage > 100 && "Excellent"} {percentage}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(percentage, 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor:
                          percentage > 100
                            ? theme.lightTheme.performanceProgressBars.excellent
                            : percentage < 60
                            ? theme.lightTheme.performanceProgressBars.poor
                            : percentage < 80
                            ? theme.lightTheme.performanceProgressBars.average
                            : theme.lightTheme.performanceProgressBars.good,
                      },
                      backgroundColor: "#e0e0e0",
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Send Report Button */}
      <Box
        onClick={handleSaveAndShare}
        sx={{
          width: isMobile ? "100%" : "250px",
          fontSize: 16,
          color: "#fff",
          mt: 2,
          cursor: "pointer",
          background:
            "linear-gradient(90deg,rgba(0, 173, 173, 1) 0%, rgba(68, 40, 160, 1) 100%)",
          textAlign: "center",
          mx: "auto",
          p: 2,
          boxShadow: "inset -5px -5px 15px #362e69",
          borderRadius: 2,
          mb: 8,
        }}
      >
        Save and share <Share />
      </Box>

      {/* Report Sent Dialog */}
      {reportDailogOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            bgcolor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(8px)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "#fff",
              width: "80%",
              p: 3,
              borderRadius: 4,
            }}
          >
            <Typography sx={{ fontSize: 18, textAlign: "center" }}>
              Tickets Report has been sent via Email
            </Typography>
            <Typography
              onClick={() => navigate("/home")}
              sx={{
                backgroundColor: theme.button.primaryBtn,
                mt: 2,
                width: 100,
                fontSize: 18,
                textAlign: "center",
                color: "#fff",
                py: 1,
                borderRadius: 3,
                cursor: "pointer",
              }}
            >
              Done
            </Typography>
          </Box>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={3000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ "&.MuiSnackbar-root": { bottom: "56px !important" } }}
      >
        <Alert
          onClose={handleClose}
          severity={snackBarConfig.severity}
          variant="filled"
        >
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const formStyle = {
  mb: 2,
  "& .MuiOutlinedInput-root": {
    "& fieldset, & .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
    "&:hover fieldset, &:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
    "&.Mui-focused fieldset, &.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.borders.secondaryBorder,
    },
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: theme.borders.secondaryBorder,
  },
};

export default DayEndReport;
